import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import Colours from '../../design-system/colours';
import useAuthorized from '../../hooks/use-authorized';
import { Resource } from '../../common/model/type/resource.enum';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { mapPhysicalProductToTableDataFormat } from '../../common/utils/services/physical-product-utils';
import { useAdminPhysicalProductMutations } from '../../hooks/use-admin-physical-product-mutations';
import { getPhysicalProductById } from '../../services/admin/admin-physical-product.service';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvDateParser from '../UI/atoms/OvDateParser';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvRecordDetailsPage from '../UI/atoms/OvRecordDetailsPage';
import OvStatusChip from '../UI/atoms/OvStatusChip';
import OvConfirmationDialog from '../UI/molecules/OvConfirmationDialog';
import OvCopyToClipboardButton from '../UI/molecules/OvCopyToClipboardButton';
import OvRecordDetailsErrorState from '../UI/molecules/OvRecordDetailsErrorState';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvTable from '../UI/molecules/OvTable';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';

const LIST_PATH = '/physical-products';

const PhysicalProductDetails = () => {
  const { t } = useTranslation();
  const { physicalProductId } = useParams() as { physicalProductId: string };
  const navigate = useNavigate();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState<boolean>(false);

  const {
    data: record,
    isFetching,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ['admin-physical-product', physicalProductId],
    queryFn: () => getPhysicalProductById(physicalProductId),
    enabled: !!physicalProductId,
  });

  const { deleteProduct, updateProduct } = useAdminPhysicalProductMutations();

  const operations = useAuthorized(Resource.PhysicalProduct);
  const updateAllowed = operations.update || operations.supervise;

  const handleOnDelete = () => {
    setIsConfirmationDialogOpen(false);
    deleteProduct.mutate(physicalProductId, {
      onSuccess: () => navigate(LIST_PATH),
    });
  };

  if (!record) {
    return (
      <OvRecordDetailsPage>
        {loadError ? (
          <OvRecordDetailsErrorState
            title={t('physicalProducts.details.errorTitle')}
            error={loadError}
            onRetry={() => refetch()}
            backLabel={t('app.subPages.menu.physicalProducts')}
            backPath={LIST_PATH}
          />
        ) : (
          <OvLoadingIndicator position="fixed" />
        )}
      </OvRecordDetailsPage>
    );
  }

  const error = loadError ?? updateProduct.error ?? deleteProduct.error;
  const isLoading =
    isFetching || updateProduct.isPending || deleteProduct.isPending;

  return (
    <OvRecordDetailsPage aria-busy={isLoading}>
      {error && <OvErrorMessage message={getAxiosErrorMessage(error)} />}

      <OvRecordDetailsCanvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('physicalProducts.details.breadcrumb')}
          backControl={
            <OvBackButton
              label={t('app.subPages.menu.physicalProducts')}
              fallbackPath={LIST_PATH}
            />
          }
          current={record.internal_name}
          title={record.internal_name}
          titleAside={
            <OvStatusChip
              label={t(
                record.is_active
                  ? 'physicalProducts.details.active'
                  : 'physicalProducts.details.inactive'
              )}
              tone={record.is_active ? 'success' : 'neutral'}
            />
          }
          meta={
            <>
              <span>
                {record.id}
                <OvCopyToClipboardButton
                  text={record.id}
                  label={t('physicalProducts.details.copyId')}
                />
              </span>
              {record.sku && <span>{record.sku}</span>}
              {record.created_at && (
                <span>
                  <OvDateParser
                    date={record.created_at}
                    format="MMM DD, YYYY - hh:mm A"
                  />
                </span>
              )}
            </>
          }
          actions={
            <OvRecordHeaderActions>
              <OvCompactButton
                tone="danger"
                icon={<DeleteIcon />}
                onClick={() => setIsConfirmationDialogOpen(true)}
              >
                {t('common.actions.delete')}
              </OvCompactButton>
            </OvRecordHeaderActions>
          }
        />

        <OvRecordDetailsSection title={t('physicalProducts.details.section')}>
          <OvTable
            data={mapPhysicalProductToTableDataFormat(record)}
            theme="Light"
            onSaveRowValue={(request: Record<string, unknown>) =>
              updateProduct.mutate({ id: physicalProductId, request })
            }
            editable={updateAllowed}
          />
        </OvRecordDetailsSection>
      </OvRecordDetailsCanvas>

      {isLoading && <OvLoadingIndicator position="fixed" />}

      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={isConfirmationDialogOpen}
        onCancel={() => setIsConfirmationDialogOpen(false)}
        onConfirm={handleOnDelete}
        description="physicalProducts.dialogs.deletePhysicalProductDescription"
        title="physicalProducts.dialogs.deletePhysicalProductTitle"
      />
    </OvRecordDetailsPage>
  );
};

export default PhysicalProductDetails;

const StyledDeleteIcon = styled(DeleteSweepIcon)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;
